import { Localized } from "@fluent/react"
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material"
import { useMemo, useState } from "react"

import { CancelButton } from "../../../../app/components/CancelButton"
import { SendButton } from "../../../../app/components/SendButton"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import {
    DocumentWorkflowStageDto,
    sendWorkflowPendingActionReminder,
} from "../documentWorkflowApi"
import { mapWorkflowActionStatus } from "../documentWorkflowHelpers"

export type SendWorkflowPendingActionReminderMode = "all" | "selected"

export interface SendWorkflowPendingActionReminderProps {
    documentId: AppId
    stage: DocumentWorkflowStageDto
    mode: SendWorkflowPendingActionReminderMode
    selectedTeamMemberId?: number | null
    onClose: () => void
    onSuccess: () => void
}

export const SendWorkflowPendingActionReminder = ({
    documentId,
    stage,
    mode,
    selectedTeamMemberId,
    onClose,
    onSuccess,
}: SendWorkflowPendingActionReminderProps) => {
    const dispatch = useAppDispatch()
    const [sending, setSending] = useState(false)
    const [serverError, setServerError] = useState("")

    const waitingMembers = useMemo(() => {
        return stage.members.filter(member => mapWorkflowActionStatus(member.actionStatus).waiting)
    }, [stage.members])

    const affectedMembersCount = useMemo(() => {
        if (mode === "all") {
            return waitingMembers.length
        }

        const isSelectedMemberWaiting = waitingMembers.some(
            member => member.documentTeamMemberId === selectedTeamMemberId
        )

        return isSelectedMemberWaiting ? 1 : 0
    }, [mode, selectedTeamMemberId, waitingMembers])

    const selectedMember = useMemo(() => {
        if (mode !== "selected") {
            return null
        }

        return stage.members.find(
            member => member.documentTeamMemberId === selectedTeamMemberId
        ) ?? null
    }, [mode, selectedTeamMemberId, stage.members])

    const handleSendClick = async () => {
        if (sending || affectedMembersCount === 0) {
            return
        }

        const forAllMembers = mode === "all"
        setSending(true)
        setServerError("")

        try {
            const result = await dispatch(
                sendWorkflowPendingActionReminder({
                    documentId,
                    dto: {
                        stageId: String(stage.id),
                        documentTeamMemberId: forAllMembers
                            ? null
                            : selectedTeamMemberId !== null && selectedTeamMemberId !== undefined
                                ? String(selectedTeamMemberId)
                                : undefined,
                        forAllMembers,
                    },
                })
            )

            if (sendWorkflowPendingActionReminder.fulfilled.match(result)) {
                dispatch(showSuccess("sent"))
                onSuccess()
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem
                    if (problem?.title) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setSending(false)
        }
    }

    return <Dialog open={true} fullWidth maxWidth="xs">
        <DialogTitle>
            <Localized id="document-reminder-title">
                <span>_Wyslij przypomnienie</span>
            </Localized>
        </DialogTitle>

        <DialogContent>
            {mode === "all"
                ? <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <Localized id="workflow-reminder-all-confirmation">
                            <span>_Czy na pewno wyslac przypomnienie o zadaniach do wykonania?</span>
                        </Localized>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <Localized
                            id="workflow-reminder-affected-count"
                            vars={{ count: affectedMembersCount }}
                        >
                            <span>_Liczba osob, do ktorych zostanie wyslane przypomnienie: {affectedMembersCount}.</span>
                        </Localized>
                    </Typography>
                </Alert>
                : <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <Localized id="workflow-reminder-selected-confirmation">
                            <span>_Przypomnienie zostanie wyslane tylko do wybranej osoby.</span>
                        </Localized>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedMember
                            ? <Localized
                                id="workflow-reminder-selected-member"
                                vars={{ memberName: selectedMember.fullName || selectedMember.userName }}
                            >
                                <span>_Przypomnienie zostanie wyslane do: {selectedMember.fullName || selectedMember.userName}.</span>
                            </Localized>
                            : <Localized id="workflow-reminder-selected-member-not-found">
                                <span>_Nie znaleziono wybranej osoby w tym etapie.</span>
                            </Localized>}
                    </Typography>
                </Alert>}

            {serverError && <Alert sx={{ mt: 1 }} onClose={() => { setServerError("") }} severity="error">
                <Localized id={serverError}>
                    <span>_Server error</span>
                </Localized>
            </Alert>}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
            <SendButton
                loading={sending}
                onClick={handleSendClick}
                disabled={sending || affectedMembersCount === 0 || (mode === "selected" && !selectedMember)}
            />
            <CancelButton loading={sending} onClick={onClose} disabled={sending} />
        </DialogActions>
    </Dialog>
}
